import { useMemo } from 'react';
import { Box, LinearProgress, Typography } from '@mui/material';

import { useServices } from '../../../hooks';
import { toLocalDateString } from '../../../lib';
import { clamp, toTimestamp, styles } from './trial-countdown-bar-helpers';

export const TrialProgressBar = (): JSX.Element | null => {
    const { data, isLoading } = useServices();

    const trial = data?.data?.trial;
    const created = data?.data?.created;
    const trialEndDate = data?.data?.trialEndDate;

    const progress = useMemo(() => {
        if (!created || !trialEndDate) return 0;

        const now = Date.now();
        const startDate = toTimestamp(created);
        const endDate = toTimestamp(trialEndDate);

        if (
            Number.isNaN(startDate) ||
            Number.isNaN(endDate) ||
            endDate <= startDate
        ) {
            return 0;
        }

        const total = endDate - startDate;
        const elapsed = now - startDate;

        return clamp((elapsed / total) * 100, 0, 100);
    }, [created, trialEndDate]);

    if (isLoading) {
        return (
            <Box sx={styles.container}>
                <LinearProgress />
            </Box>
        );
    }

    if (!trial || !created || !trialEndDate) {
        return null;
    }

    const endTimestamp = toTimestamp(trialEndDate);
    const hasEnded = Date.now() > endTimestamp;

    const barText = hasEnded
        ? `Your Free Trial Expired on ${toLocalDateString(endTimestamp)}`
        : `Your Free Trial Expires on ${toLocalDateString(endTimestamp)}`;

    return (
        <Box sx={styles.container}>
            <Box flexGrow={1} sx={{ position: 'relative' }}>
                <LinearProgress
                    variant='determinate'
                    value={hasEnded ? 100 : progress}
                    sx={{
                        ...styles.progressBar,
                        ...(hasEnded && styles.progressBarError),
                    }}
                />

                <Box sx={styles.barText}>
                    <Typography
                        variant='body2'
                        fontWeight={500}
                        sx={hasEnded ? styles.errorText : styles.normalText}
                    >
                        {barText}
                    </Typography>
                </Box>
            </Box>
        </Box>
    );
};
